import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, DownloadIcon } from '@chakra-ui/icons';
import { MdOutlineFileUpload } from 'react-icons/md';
import Page from '../components/common/Page';
import Api from '../lib/Api';
import urlConstants from '../lib/constants/url.constants';
import useToastNotification from '../lib/hooks/useToastNotification';

type Program = {
  _id: string;
  title: string;
};

type MigrationResult = {
  dryRun: boolean;
  inputCount: number;
  validEmailCount: number;
  duplicateCount: number;
  invalidEmails: string[];
  usersFound: number;
  usersNotFound: number;
  notFoundEmails: string[];
  selectedPrograms: Program[];
  usersToUpdate: number;
  assignmentsToAdd: number;
  assignmentsSkipped: number;
  usersUpdated?: number;
  message?: string;
};

const extractFirstColumn = (line: string) => {
  const value = line.trim();
  if (!value.startsWith('"')) return value.split(',')[0].trim();

  let result = '';
  for (let index = 1; index < value.length; index += 1) {
    if (value[index] !== '"') {
      result += value[index];
      continue;
    }
    if (value[index + 1] === '"') {
      result += '"';
      index += 1;
      continue;
    }
    break;
  }
  return result.trim();
};

const parseEmailCsv = (content: string) =>
  content
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map(extractFirstColumn)
    .filter(Boolean);

const getErrorMessage = (error: any, fallback: string) => {
  if (typeof error === 'string') return error;
  return error?.message || fallback;
};

const csvEscape = (value: string) => `"${value.replace(/"/g, '""')}"`;

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' p={3} minW={0}>
    <Text fontSize='xs' color='gray.500' textTransform='uppercase'>
      {label}
    </Text>
    <Text fontSize='xl' fontWeight='semibold'>
      {value}
    </Text>
  </Box>
);

const BulkMigration = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState<string[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<MigrationResult | null>(null);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const showToast = useToastNotification();

  useEffect(() => {
    const loadPrograms = async () => {
      const response = await Api.get<any>(urlConstants.programInfo.getAllSections);
      if (response.error) {
        showToast({
          title: 'Unable to load programs',
          description: getErrorMessage(response.error, 'Please refresh and try again'),
          status: 'error',
        });
      } else {
        const sections = Array.isArray(response.data)
          ? response.data
          : response.data?.sections;
        setPrograms(
          (sections || []).map((program: any) => ({
            _id: program._id,
            title: program.title,
          }))
        );
      }
      setIsLoadingPrograms(false);
    };

    loadPrograms();
  }, [showToast]);

  const invalidatePreview = () => {
    setPreview(null);
    setResult(null);
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showToast({ title: 'Invalid file', description: 'Please select a CSV file', status: 'error' });
      event.target.value = '';
      return;
    }

    const parsedEmails = parseEmailCsv(await file.text());
    if (parsedEmails.length === 0) {
      showToast({ title: 'Empty CSV', description: 'No email addresses found', status: 'error' });
      event.target.value = '';
      return;
    }
    if (parsedEmails.length > 10000) {
      showToast({ title: 'CSV too large', description: 'Maximum 10,000 rows allowed', status: 'error' });
      event.target.value = '';
      return;
    }

    setEmails(parsedEmails);
    setFileName(file.name);
    invalidatePreview();
  };

  const toggleProgram = (programId: string) => {
    setSelectedProgramIds(current =>
      current.includes(programId)
        ? current.filter(id => id !== programId)
        : [...current, programId]
    );
    invalidatePreview();
  };

  const requestMigration = async (dryRun: boolean) => {
    const response = await Api.post<MigrationResult>(urlConstants.users.bulkMigration, {
      emails,
      programIds: selectedProgramIds,
      dryRun,
      confirmed: !dryRun,
      fileName,
    });
    if (response.error) {
      throw new Error(getErrorMessage(response.error, 'Migration request failed'));
    }
    return response.data as MigrationResult;
  };

  const handlePreview = async () => {
    setIsPreviewing(true);
    try {
      const data = await requestMigration(true);
      setPreview(data);
      setResult(null);
    } catch (error: any) {
      showToast({ title: 'Preview failed', description: error.message, status: 'error' });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const data = await requestMigration(false);
      setResult(data);
      setPreview(data);
      onClose();
      showToast({
        title: 'Migration complete',
        description: `${data.usersUpdated || 0} users updated`,
        status: 'success',
      });
    } catch (error: any) {
      showToast({ title: 'Migration failed', description: error.message, status: 'error' });
    } finally {
      setIsApplying(false);
    }
  };

  const downloadMissingUsers = () => {
    const missing = (result || preview)?.notFoundEmails || [];
    const blob = new Blob(
      [`email\n${missing.map(csvEscape).join('\n')}\n`],
      { type: 'text/csv;charset=utf-8' }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-migration-users-not-found.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const selectedLabel = selectedProgramIds.length === 0
    ? 'Select programs'
    : `${selectedProgramIds.length} program${selectedProgramIds.length === 1 ? '' : 's'} selected`;
  const canPreview = emails.length > 0 && selectedProgramIds.length > 0;
  const canApply = Boolean(preview && preview.assignmentsToAdd > 0 && !result);
  const summary = result || preview;

  return (
    <Page>
      <VStack align='stretch' spacing={5} maxW='1100px'>
        <Box>
          <Heading size='lg'>Bulk Migration</Heading>
          <Text color='gray.600' mt={1}>
            Add selected programs as Legacy to existing CMS users.
          </Text>
        </Box>

        <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' p={{ base: 4, md: 5 }}>
          <VStack align='stretch' spacing={5}>
            <FormControl>
              <FormLabel>Email CSV</FormLabel>
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv,text/csv'
                hidden
                onChange={handleFile}
              />
              <HStack flexWrap='wrap'>
                <Button
                  leftIcon={<MdOutlineFileUpload />}
                  variant='outline'
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose CSV
                </Button>
                <Text color={fileName ? 'gray.700' : 'gray.500'}>
                  {fileName || 'No file selected'}
                </Text>
                {emails.length > 0 && <Text color='gray.500'>({emails.length} rows)</Text>}
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>Programs</FormLabel>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  rightIcon={isLoadingPrograms ? <Spinner size='xs' /> : <ChevronDownIcon />}
                  variant='outline'
                  minW={{ base: '100%', md: '320px' }}
                  textAlign='left'
                  isDisabled={isLoadingPrograms}
                >
                  {selectedLabel}
                </MenuButton>
                <MenuList maxH='320px' overflowY='auto' minW='360px'>
                  {programs.map(program => (
                    <MenuItem key={program._id} onClick={() => toggleProgram(program._id)}>
                      <Checkbox
                        isChecked={selectedProgramIds.includes(program._id)}
                        pointerEvents='none'
                        mr={3}
                      />
                      <Text noOfLines={1}>{program.title}</Text>
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </FormControl>

            <Flex justify='flex-end'>
              <Button
                colorScheme='primary'
                onClick={handlePreview}
                isLoading={isPreviewing}
                isDisabled={!canPreview || isApplying}
              >
                Preview Migration
              </Button>
            </Flex>
          </VStack>
        </Box>

        {summary && (
          <Box borderWidth='1px' borderColor='gray.200' borderRadius='md' p={{ base: 4, md: 5 }}>
            <Flex justify='space-between' align='center' gap={3} mb={4} flexWrap='wrap'>
              <Heading size='sm'>{result ? 'Migration Result' : 'Migration Preview'}</Heading>
              {summary.usersNotFound > 0 && (
                <Button
                  size='sm'
                  variant='outline'
                  leftIcon={<DownloadIcon />}
                  onClick={downloadMissingUsers}
                >
                  Download Missing Users
                </Button>
              )}
            </Flex>

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              <Stat label='Valid emails' value={summary.validEmailCount} />
              <Stat label='Duplicates' value={summary.duplicateCount} />
              <Stat label='Invalid' value={summary.invalidEmails.length} />
              <Stat label='Users found' value={summary.usersFound} />
              <Stat label='Users not found' value={summary.usersNotFound} />
              <Stat label='Users to update' value={summary.usersToUpdate} />
              <Stat label='Programs to add' value={summary.assignmentsToAdd} />
              <Stat label='Already assigned' value={summary.assignmentsSkipped} />
            </SimpleGrid>

            <Divider my={4} />
            {result ? (
              <Alert status='success' borderRadius='md'>
                <AlertIcon />
                Migration completed. {result.usersUpdated || 0} users updated.
              </Alert>
            ) : summary.assignmentsToAdd === 0 ? (
              <Alert status='info' borderRadius='md'>
                <AlertIcon />
                No new program assignments are needed.
              </Alert>
            ) : (
              <Flex justify='flex-end'>
                <Button colorScheme='primary' onClick={onOpen} isDisabled={!canApply}>
                  Apply Migration
                </Button>
              </Flex>
            )}
          </Box>
        )}
      </VStack>

      <Modal isOpen={isOpen} onClose={isApplying ? () => undefined : onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Bulk Migration</ModalHeader>
          <ModalCloseButton isDisabled={isApplying} />
          <ModalBody>
            <Text>
              Add {preview?.assignmentsToAdd || 0} legacy program assignments to{' '}
              {preview?.usersToUpdate || 0} users?
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant='ghost' onClick={onClose} isDisabled={isApplying}>Cancel</Button>
            <Button colorScheme='primary' onClick={handleApply} isLoading={isApplying}>
              Confirm Migration
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Page>
  );
};

export default BulkMigration;
